import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Menu, MenuItem } from "@mui/material";

import styles from "./Question.module.scss";
import QuestionModal from "../Question/Components/AddQuestions";
import { fetchQuestionsSet } from "@/store/slices/QuestionSlice";
import type { RootState } from "@/store/rootReducer";
import type { AppDispatch } from "@/store/store";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import { MODAL_ID } from "@/lib/constants";
import { setModalOpen } from "@/store/slices/globalSlice";
import { deleteQuestionAPI } from "@/services/api/Question";
import Table from "@/components/Table/Table";
import Pagination from "@/components/Table/Pagination";
import SearchBox from "@/components/SearchBox/SearchBox";
import PageLayout from "@/components/PageLayout/PageLayout";
import EditIcon from "@/components/SVGComponents/EditIcon";
import DeleteIcon from "@/components/SVGComponents/DeleteIcon";

const Question: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { questionSet, totalRecords, loading } = useSelector(
    (state: RootState) => state.questions
  );

  console.log("questions", questionSet);


  const [openModal, setOpenModal] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [difficultyLevel, setDifficultyLevel] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);


  const handleDelete = async () => {
    try {
      if (!selectedQuestion?.id) return;

      await deleteQuestionAPI(selectedQuestion.id);

      dispatch(setModalOpen({ id: null, data: null }));

      dispatch(fetchQuestionsSet({ pageNo: page, perPage, search: debouncedSearch, difficultyLevel }));

    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  const stripHtml = (html: string) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const truncateText = (text: string, maxLength = 30) => {
    if (!text) return "";
    const plainText = stripHtml(text);
    return plainText.length > maxLength
      ? `${plainText.slice(0, maxLength)}...`
      : plainText;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 on new search
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    dispatch(fetchQuestionsSet({ pageNo: page, perPage, search: debouncedSearch, difficultyLevel }));
  }, [dispatch, page, debouncedSearch, difficultyLevel]);

  const columns = [
    {
      name: "Name",
      selector: (row: any) => stripHtml(row.name),
      cell: (row: any) => (
        <div title={row.name}>{truncateText(row.name, 30)}</div>
      ),
      sortable: true,
    },
    // {
    //   name: "Description",
    //   selector: (row: any) => stripHtml(row.description),
    //   cell: (row: any) => (
    //     <div title={row.description}>{truncateText(row.description, 30)}</div>
    //   ),
    //   sortable: true,
    // },
    {
      name: "Difficulty",
      cell: (row: any) => (
        <span
          className={
            row.difficultyLevel === "EASY"
              ? styles.badgeEasy
              : row.difficultyLevel === "MEDIUM"
                ? styles.badgeMedium
                : styles.badgeHard
          }
        >
          {row.difficultyLevel}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row: any) => (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span
            className={styles.actionIcon}
            onClick={() => {
              setEditId(row.id);
              setOpenModal(true);
            }}
            style={{ cursor: "pointer" }}
            title="Edit"
          >
            <EditIcon/>
          </span>
          <button
            onClick={() => {
              setSelectedQuestion(row);
              dispatch(
                setModalOpen({
                  id: MODAL_ID.CONFIRM_DELETE,
                  data: { userName: row.name, used: row.used },
                })
              );
            }}
            title="Delete"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <DeleteIcon/>
          </button>
        </div>
      ),
      width: "100px",
    }
  ];

  return (
    <PageLayout
      title="Question sets"
      headerAction={
        <button
          className={styles.newBtn}
          onClick={() => setOpenModal(true)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: 6}}>
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          New question set
        </button>
      }
      innerActions={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <SearchBox
            value={search}
            onChange={(val: string) => setSearch(val)}
            // placeholder="Search question set..."
            width={240}
            onFilterClick={(e: any) => setAnchorEl(e.currentTarget)}
          />
          <Menu
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {/* <MenuItem onClick={() => { setDifficultyLevel(""); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === ""}>All Difficulties</MenuItem> */}
            <MenuItem onClick={() => { setDifficultyLevel("EASY"); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === "EASY"}>Easy</MenuItem>
            <MenuItem onClick={() => { setDifficultyLevel("MEDIUM"); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === "MEDIUM"}>Medium</MenuItem>
            <MenuItem onClick={() => { setDifficultyLevel("HARD"); setAnchorEl(null); setPage(1); }} selected={difficultyLevel === "HARD"}>Hard</MenuItem>
          </Menu>
        </div>
      }
    >
      {/* TABLE */}
      <div>
        <Table 
          columns={columns} 
          data={Array.isArray(questionSet) ? questionSet : []} 
          progressPending={loading}
        />
        <Pagination 
          totalCount={totalRecords}
          pageSelected={page}
          setPageSelected={(p) => {
            setPage(p);
            dispatch(fetchQuestionsSet({ pageNo: p, perPage, search: debouncedSearch, difficultyLevel }));
          }}
          rowsPerPageValue={perPage}
        />
      </div>

      {/* MODAL */}
      {openModal && (
        <QuestionModal
          onClose={() => {
            setOpenModal(false);
            setEditId(null);
          }}
          editId={editId}
          refreshList={() =>
            dispatch(fetchQuestionsSet({ pageNo: page, perPage, search: debouncedSearch, difficultyLevel }))
          }
        />
      )}
      <ConfirmDeleteModal
        deleteLabel="Question Set"
        handleDelete={handleDelete}
      />
    </PageLayout>
  );
};

export default Question;